import { Injectable } from '@angular/core';
import { FormGroup, FormArray, Validators } from '@angular/forms';

import { Subject, Subscription } from 'rxjs';
import { startWith, takeWhile, takeUntil, filter } from 'rxjs/operators';

import { filesizeValidator, minDateValidator, allOfValidator } from './validators';

import { ConfigMap, FormItemMap, ConfigKey, ExtendedKey } from './configs';
import { AppConfig, FormItem, AppConfigProperty } from './app.config';
// import { minDateTimeValidator } from './validators/mindatetime.validator';

@Injectable({
   providedIn: 'root'
})
export class AppConfigService {

   private _configs = ConfigMap;
   private _formItems = FormItemMap;
   _subscriptions: {[ key: string ]: Subscription } = {};

   private _config: AppConfig;
   get config() { return this._config; }

   private _configObj: any;
   get configObj() { return this._configObj; }

   configLoaded$ = new Subject<void>();

   constructor() {
      this.load();
   }

   load( config: string = 'default' ) {
      if ( !this._configs[config] ) {
         config = 'default';
      }

      this._config = this.loadConfig( config as ConfigKey );
      this._configObj = this.fromJSON( this._config );

      // console.log( 'loaded opts', this._config );
      // console.log( 'fromJSON', this.fromJSON( this._config ));

      this.configLoaded$.next();
   }

   private loadConfig( config: ConfigKey ) {
      const opts = this._configs[config];

      opts.booking = this.mergeItems( opts.booking );

      if ( opts.extends ) {
         const extended = this.loadConfig( opts.extends as ConfigKey );

         if ( opts?.activityFeedRoutes || extended?.activityFeedRoutes ) {
            opts.activityFeedRoutes = ({ ...extended?.activityFeedRoutes, ...opts?.activityFeedRoutes });
         }

         if ( opts?.booking || extended?.booking ) {
            // merge booking
            opts.booking = this.mergeItems( extended?.booking, opts?.booking );
         }
      }

      return opts;
   }

   private mergeItems( orig: FormItem[] = [], extended: FormItem[] = [] ) {
      const merged = [ ...orig ];

      merged.filter( obj => obj.extends ).map( obj => {
         obj.items = this.mergeItems( this._formItems[ obj.extends ], obj.items );
      });

      extended.map( obj => {
         const origItem = orig.filter( o => o.id === obj.id )[0];

         const mergedItem = { ...origItem, ...obj };

         if ( obj?.extends ) {
            obj.items = this.mergeItems( obj.items, this._formItems[ obj.extends ] );
         }

         if ( obj?.items?.length ) {
            const items = this.mergeItems( origItem.items, obj.items );
            mergedItem.items = items;
         }

         if ( !origItem ) {
            merged.push( mergedItem );
         } else {
            merged.splice( merged.indexOf( origItem ), 1, mergedItem );
         }
      });

      return merged;
   }

   getExtendedConfig( property: ExtendedKey ): FormItem[] {
      return this._formItems[property];
   }

   getProperty( items: FormItem[], property: string ): AppConfigProperty {
      const properties = {};

      items?.filter( item => !!item[property] ).map( item => properties[item.id] = item[property] );

      items?.filter( item => item?.extends || item?.items ).map( item => {
         const prop = this.getProperty( item?.items, property );
         if ( prop ) { properties[ item.id ] = prop; }
      });

      items?.filter( item => item?.at ).map( item => {
          item.at.map( i => {
            const prop = this.getProperty( i?.items, property );

            if ( prop ) {
               if ( typeof properties[ item.id ] !== 'object' ) { properties[ item.id ] = {}; }
               if ( typeof properties[ item.id ][ 'at' ] !== 'object' ) { properties[ item.id ][ 'at' ] = {}; }
               properties[ item.id ][ 'at' ][ i.index ] = prop;
            }
         });
      });

      if ( !Object.keys( properties ).length ) { return undefined; }

      return properties;
   }

   /**
    * Recusive function for setting validators on a FormGroup based on a JSON config file
    */
   setValidators( group: FormGroup, config: FormItem[], update = true ): FormGroup {
      config.map( c => {
         const control = c?.id ? group.get( c.id ) : group;

         if ( !control ) { return; }

         if ( c?.display === false ) {
            // For display false we are just removing the control
            group.removeControl( c.id );

            return;
         }

         if ( c?.default ) { control.setValue( c.default ); }
         if ( c?.disable ) { control.disable(); }

         // let's create the validators for the loaded control
         control.clearValidators();

         // checking for sub-items in the config
         if ( c?.items || c?.at || c?.allOf ) {
            if ( control instanceof FormGroup ) {
               // recursive setting of config from here
               this.setValidators( control, c?.items );
            }

            if ( control instanceof FormArray ) {
               if ( this._subscriptions[ c.id ] ) {
                  this._subscriptions[ c.id ].unsubscribe();
               }

               let arrayLength = 0;
               this._subscriptions[ c.id ] = control.valueChanges.pipe(
                  startWith( control.value ),
                  filter( value => value.length !== arrayLength ),
                  takeWhile( _ => group?.contains( c.id )),
                  takeUntil( this.configLoaded$ ),
               ).subscribe( value => {
                  arrayLength = value.length;
                  value.map(( _, i ) => {
                     const currentControl = control.at( i );
                     const at = c?.at?.filter( at => at.index === i )[0];

                     currentControl.clearValidators();
                     this.setValidators( currentControl as FormGroup, this.mergeItems( at?.items, c?.items ));
                     currentControl.addValidators( allOfValidator( at?.allOf ?? c?.allOf ));
                  });
               });
            }
         }

         // group level validators
         if ( c?.allOf && control instanceof FormGroup ) { control.addValidators( allOfValidator( c.allOf )); }

         if ( c?.required ) { control.addValidators( Validators.required ); }
         if ( c?.requireTrue ) { control.addValidators( Validators.requiredTrue ); }
         if ( c?.pattern ) { control.addValidators( Validators.pattern( c?.pattern )); }
         if ( c?.email ) { control.addValidators( Validators.email ); }
         if ( c?.min ) { control.addValidators( Validators.min( c?.min )); }
         if ( c?.max ) { control.addValidators( Validators.max( c?.max )); }
         if ( c?.minLength ) { control.addValidators( Validators.minLength( c?.minLength )); }
         if ( c?.maxLength ) { control.addValidators( Validators.maxLength( c?.maxLength )); }

         // custom validators
         if ( c?.filesize ) { control.addValidators( filesizeValidator( c?.filesize )); }
         if ( c?.minDate ) { control.addValidators( minDateValidator( group.get( c?.minDate ))); }
         // if ( c?.minDateTime ) { control.addValidators( minDateTimeValidator( group.get( c?.minDateTime )))}
      });

      if ( update ) {
         group.updateValueAndValidity();
      }

      return group;
   }

   fromJSON( configs: FormItem[] | FormItem, _defaults: FormItem = {} ) {
      // console.log( 'fromJSON', configs, _defaults );

      if ( configs instanceof String ) { return configs; }

      if ( Array.isArray( configs )) {
         // console.log( 'config is array' );
         const config = {};

         configs.map( c => config[ c.id ] = this.fromJSON( c ));

         return config;
      } else {
         // console.log( 'config is not an array' );
         const obj: any = {};

         Object.keys( configs ).filter( key => ![ 'id', 'extends', 'items', 'at', 'index', 'enum' ].includes( key ))
            .map( key => obj[ key ] = Array.isArray( configs[key] ) ? this.fromJSON( configs[key]) : configs[key] );

         // enums
         if ( configs?.enum ) { obj.enum = configs.enum; }

         // items
         configs?.items?.map( item => {
            if ( !obj?.items ) { obj.items = {}; }

            obj.items[ item.id ] = this.fromJSON( item );
         });

         // at
         configs?.at?.map( at => {
            if ( !obj?.at ) { obj.at = {}; }
            obj.at[ at.index ] = this.fromJSON( at );
         });

         return obj;
      }
   }
}
